/**
 * @class Connection
 */

import * as React from "react";
import { IComponent, IConnection } from "./interfaces/Component";
import { Text, Arrow, Group, Circle, Image as ReactKonvaImage } from "react-konva";
import {
  Line as KonvaLine,
  Text as KonvaText,
} from "konva";
import { componentWidth, componentHeight, computeXAngle } from "./utilities";
import { IPort } from "./interfaces/ComponentDefinition";

export type ConnectionProps = {
  newConnection: false;
  fromComponent: IComponent;
  toComponent: IComponent | undefined;
  connection: IConnection;
  onClick(component: IComponent, connection: IConnection): void;
  index: number;
  connectionsSharingDirection: Array<IConnection | any>;
  connectionsSharingPath: Array<IConnection | any>;
};

export type NewConnectionProps = {
  newConnection: true;
  fromComponent: IComponent;
  fromPort: IPort;
  mousePosition: {
    x: number;
    y: number;
  };
  componentMousedOver: IComponent | null;
};

export type Props = ConnectionProps | NewConnectionProps;

export type State = {
  hovering: boolean;
  mouseX: number;
  mouseY: number;
  closeImageLoaded: boolean;
};

const Options = {
  componentWidth: componentWidth,
  componentHeight: componentHeight,
  iconWidth: 35,
  iconHeight: 25
};

export default class Connection extends React.Component<Props, State> {
  node: any;
  text: any;
  closeImage: HTMLImageElement;
  state = {
    hovering: false,
    mouseX: 0,
    mouseY: 0,
    closeImageLoaded: false
  };
  
  componentDidMount() {
    try {
      const linePosition = this.getScaledPointerPosition();
      if (linePosition) {
        this.setState({
          mouseX: linePosition.x,
          mouseY: linePosition.y
        });
      }
    } catch(e) {/* ignore */}
    this.closeImage = new Image(Options.iconWidth);
    this.closeImage.onload = () => {
      this.setState({ closeImageLoaded: true });
    };
    this.closeImage.src = `/assets/images/close-white.svg`;
  }

  onNode = (node: any) => {
    if (!this.node) {
      this.node = node;
    }
    if (this.node) {
      (this.node as KonvaLine).moveToBottom();
    }
  };

  onHoverChange = (hovering: boolean) => {
    if (hovering) {
      try {
        const { x: mouseX, y: mouseY } = this.getScaledPointerPosition();
        this.setState({ hovering, mouseX, mouseY });
      } catch(e) {/* ignore */}
    } else {
      setTimeout(() => {
        this.setState({
          hovering
        });
      }, 200);
    }
  };

  getScaledPointerPosition = () => {
    const stage = (this.node as KonvaLine).getStage();
    const pointerPosition = stage.getPointerPosition();
    const stageX = stage.getAttr("x");
    const stageY = stage.getAttr("y");
    const stageScaleX = stage.getAttr("scaleX");
    const stageScaleY = stage.getAttr("scaleY");
    const x = (pointerPosition.x - stageX) / stageScaleX;
    const y = (pointerPosition.y - stageY) / stageScaleY;
    return { x, y };
  };

  onText = (text: KonvaText, i: number) => {
    const props = this.props;
    if (props.newConnection) return;
    if (text) {
      const spacing = 5; // spacing between labels
      const tCount = props.connectionsSharingPath.length;
      const tHWidth = (text as KonvaText).getWidth() / 2;
      let offsetX = tHWidth;
      if (tCount > 2) {
        let indexEven = (1 + i) % 2 === 0;
        offsetX = tCount % 2 === 0
          ? tHWidth - (i * (spacing + tHWidth)) + ((spacing + tHWidth) * (
            indexEven ? tCount / 2 : (tCount / 2) - 1
          ))
          : tHWidth - (i * (spacing + tHWidth)) + (tHWidth * tCount / 2);
      }
      (text as KonvaText).offsetX(offsetX);
    }
  };

  render() {
    const props: any = this.props;

    if (props.fromComponent === undefined) return null;

    // const definition = componentsByType[props.fromComponent.graphComponentId];
    // const { ports } = definition;
  
    const fromX = props.fromComponent.x;
    const fromY = props.fromComponent.y;

    let toX = fromX;
    let toY = fromY;

    if (props.newConnection) {

      if (props.componentMousedOver === null) {
        toX = props.mousePosition.x !== -1 ? props.mousePosition.x : props.fromComponent.x;
        toY = props.mousePosition.y !== -1 ? props.mousePosition.y : props.fromComponent.y;
      } else {
        toX = props.componentMousedOver.x;
        toY = props.componentMousedOver.y;
      }

    } else if (props.toComponent) {
      toX = props.toComponent.x
      toY = props.toComponent.y;
    } else return null

    const height = toY - fromY;
    const width = toX - fromX;
    
    const connectionAngle = computeXAngle(fromX, fromY, toX, toY);
    let connectionAngleX = connectionAngle;
    let connectionAngleY = connectionAngle;
    
    let connectionAngleForText = connectionAngle;

    if (toX < fromX) {
      connectionAngleForText = connectionAngleForText - 180;
      connectionAngleY = Math.abs(connectionAngleY - 180);
    }
    if (toY < fromY) {
      connectionAngleForText = -connectionAngleForText;
      connectionAngleY = -connectionAngleY;
    }

    let connectorDotOffsetY =
      Math.tan(connectionAngleY * Math.PI / 180) * componentWidth / 2;

    connectorDotOffsetY =
      connectorDotOffsetY < 0
        ? Math.max(connectorDotOffsetY, -componentHeight / 2)
        : Math.min(connectorDotOffsetY, componentHeight / 2);

    let connectorDotOffsetX =
      componentHeight / 2 / Math.tan(connectionAngleX * Math.PI / 180);
  
    connectorDotOffsetX =
      connectorDotOffsetX < 0
        ? Math.max(connectorDotOffsetX, -componentWidth / 2)
        : Math.min(connectorDotOffsetX, componentWidth / 2);

    const connectorDotX = fromX + connectorDotOffsetX;
    const connectorDotY = fromY + connectorDotOffsetY;

    /*
     * All connections are rendered with this component
     * indexToRender helps determine which connections are made visible,
     * avoiding blurriness and conflicts when they share a path
     * 
     */
    let indexToRender = 0;

    if (props.newConnection === false) {
      toX = toX - connectorDotOffsetX * 1.2;
      toY = toY - connectorDotOffsetY * 1.2;
      indexToRender = props.connectionsSharingDirection.length - 1;
    } else if (
      props.componentMousedOver !== null &&
      props.componentMousedOver.id !== props.fromComponent.id
    ) {
      toX = props.componentMousedOver.x - connectorDotOffsetX * 1.2;
      toY = props.componentMousedOver.y - connectorDotOffsetY * 1.2;
    }

    const existingConnection = (props: any) => (
      <React.Fragment>
        <Group
          onMouseOver={e => {
            /* this.onHoverChange(true); */
            this.setState({ hovering: true });
            e.cancelBubble = true;
          }}
          onMouseOut={e => {
            /* this.onHoverChange(false); */
            this.setState({ hovering: false });
            e.cancelBubble = true;
          }}
        >
          {(
            <Arrow
              stroke="grey"
              strokeWidth={3}
              points={[fromX, fromY, toX, toY]}
              tension={0.4}
              visible={props.index === indexToRender}
            />
          )}
          {(
            (props.connectionsSharingPath).map((c: any, i: number) => {
              const index = i + 1;
              const extension = this.getTextExtension(index);
              return (
                <Text
                  key={`${c.fromPortId}-${c.toComponentId}-${i}`}
                  ref={(text: KonvaText) => this.onText(text, i)}
                  x={fromX + width/2}
                  y={fromY + height/2}
                  offsetY={index % 2 === 1 ? 20 : -10}
                  text={`${c.port.title.toUpperCase()}${extension}`}
                  rotation={connectionAngleForText}
                  fill="grey"
                  visible={
                    props.index === indexToRender &&
                    /* this.state.hovering === false && */
                    (Math.abs(width) > componentWidth * 1.7 || Math.abs(height) > componentWidth * 1.7) &&
                    // The following check avoids conflicts when we have connections ...
                    // sharing a path but face opposite directions
                    props.fromComponent.id === props.connectionsSharingPath[
                      props.connectionsSharingPath.length - 1
                    ].fromComponentId
                  }
                />
              );
            })
          )}
          <Group
            onClick={() =>
              props.onClick(props.connection)
            }
            x={fromX + width/2}
            y={fromY + height/2}
            visible={
              props.index === indexToRender &&
              this.state.hovering &&
              // The following check avoids conflicts when we have connections ...
              // sharing a path but face opposite directions
              props.fromComponent.id === props.connectionsSharingPath[
                props.connectionsSharingPath.length - 1
              ].fromComponentId
            }
          >
            <Circle
              fill="red"
              radius={12}
              shadowBlur={10}
              shadowOpacity={0.5}
              stroke="white"
              strokeWidth={1}
            />
            {this.state.closeImageLoaded && (
              <ReactKonvaImage
                image={this.closeImage}
                offsetX={5}
                offsetY={5}
                width={10}
                height={10}
              />
            )}
          </Group>
        </Group>
        {
          <Group x={connectorDotX} y={connectorDotY} visible={props.index === indexToRender}>
            {/* controls spacing between connections and their fromX, fill color is same as Stage container background */}
            <Circle radius={14} fill="#f7f7f7" />
            {/* connector dot */}
            <Circle radius={7} fill="skyblue" />
          </Group>
        }
      </React.Fragment>
    );

    const newConnection = () => (
      <React.Fragment>
        <Arrow
          stroke="grey"
          strokeWidth={3}
          points={[fromX, fromY, toX, toY]}
          tension={0.4}
          ref={this.onNode}
          lineCap="round"
        />
      </React.Fragment>
    )
    
    return (
      props.newConnection ? newConnection() : existingConnection(props)
    );
  }

  getTextExtension(index: number) {
    let similarConnectionsExist: boolean = false;
    const props: any = this.props;

    props.connectionsSharingDirection.forEach((connection: IConnection) => {
      if (similarConnectionsExist === false) {
        if (props.connectionsSharingDirection.filter((c: IConnection) =>
          c.fromPortId === connection.fromPortId
        ).length > 1) {
          similarConnectionsExist = true;
        }
      }
    });
    
    return similarConnectionsExist
      ? `-${index}`
      : ``;
  }
}
